// Global keyboard shortcuts for FlowchartBuilder (copy/cut/paste, undo/redo,
// delete selection, save, quick-connect toggle), extracted out of the
// component body. Pure effect-registration hook — no internal state, nothing
// returned.
import { useEffect } from 'react';
import type { Box, Arrow, ClickConnectMode } from './types/index';

interface UseKeyboardShortcutsParams {
  isActiveRef: React.MutableRefObject<boolean>;
  isEditingText: boolean;
  selectedBoxes: number[];
  setSelectedBoxes: (ids: number[]) => void;
  boxes: Box[];
  arrows: Arrow[];
  setBoxes: React.Dispatch<React.SetStateAction<Box[]>>;
  setArrows: React.Dispatch<React.SetStateAction<Arrow[]>>;
  copySelectedBoxes: (cut?: boolean) => void;
  pasteBoxes: () => void;
  undo: () => void;
  redo: () => void;
  pushHistoryEntry: (newBoxes: Box[], newArrows: Arrow[]) => void;
  saveRef: React.MutableRefObject<() => Promise<void>>;
  clickConnectMode: ClickConnectMode | null;
  setClickConnectMode: (m: ClickConnectMode | null) => void;
  setContextMenu: (v: null) => void;
  setLinkPanel: (v: null) => void;
}

export function useKeyboardShortcuts(params: UseKeyboardShortcutsParams): void {
  const {
    isActiveRef, isEditingText, selectedBoxes, setSelectedBoxes, boxes, arrows, setBoxes, setArrows,
    copySelectedBoxes, pasteBoxes, undo, redo, pushHistoryEntry, saveRef,
    clickConnectMode, setClickConnectMode, setContextMenu, setLinkPanel,
  } = params;

  // Delete functionality for selected boxes + Undo/Redo shortcuts + Copy/Paste
  useEffect(() => {
    /**
     * Handles keyboard shortcuts (Copy, Paste, Undo, Redo, Delete, Quick Connect)
     * @param e - Keyboard event
     */
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (!isActiveRef.current) return;
      if (e.key === 'Escape') { setContextMenu(null); setLinkPanel(null); }

      // Don't intercept canvas shortcuts if focus is in a native input/textarea/select
      const target = e.target as HTMLElement;
      const nativeInputFocused = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable;

      // Copy: Ctrl+C (Windows/Linux) or Cmd+C (Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 'c' && !isEditingText && !nativeInputFocused) {
        e.preventDefault();
        copySelectedBoxes();
        return;
      }

      // Cut: Ctrl+X (Windows/Linux) or Cmd+X (Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 'x' && !isEditingText && !nativeInputFocused) {
        e.preventDefault();
        copySelectedBoxes(true);
        return;
      }

      // Paste: Ctrl+V (Windows/Linux) or Cmd+V (Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 'v' && !isEditingText && !nativeInputFocused) {
        e.preventDefault();
        pasteBoxes();
        return;
      }

      // Undo: Ctrl+Z (Windows/Linux) or Cmd+Z (Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey && !nativeInputFocused) {
        e.preventDefault();
        undo();
        return;
      }

      // Redo: Ctrl+Y (Windows/Linux) or Cmd+Shift+Z (Mac)
      if ((((e.ctrlKey || e.metaKey) && e.key === 'y') ||
          ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z')) && !nativeInputFocused) {
        e.preventDefault();
        redo();
        return;
      }

      // Delete selected boxes - only with Delete key, not Backspace
      // And not when editing text
      if (e.key === 'Delete' && selectedBoxes.length > 0 && !isEditingText && !nativeInputFocused) {
        // Prevent default browser behavior
        e.preventDefault();

        // Remove all selected boxes and their connected arrows, as one undo step.
        const newBoxes = boxes.filter(b => !selectedBoxes.includes(b.id));
        const newArrows = arrows.filter(a =>
          !selectedBoxes.includes(a.fromBox) && !selectedBoxes.includes(a.toBox)
        );
        pushHistoryEntry(newBoxes, newArrows);
        setBoxes(newBoxes);
        setArrows(newArrows);

        // Clear selection
        setSelectedBoxes([]);
      }

      // Save: Ctrl+S (Windows/Linux) or Cmd+S (Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        saveRef.current();
        return;
      }

      // Toggle Quick Connect mode with Ctrl+Q or Cmd+Q (when not editing text)
      if ((e.ctrlKey || e.metaKey) && e.key === 'q' && !isEditingText) {
        e.preventDefault();
        // Toggle Quick Connect mode
        if (clickConnectMode === null) {
          setClickConnectMode({}); // Enable mode
        } else {
          setClickConnectMode(null); // Disable mode
        }
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedBoxes, boxes, arrows, undo, redo, pushHistoryEntry, isEditingText, copySelectedBoxes, pasteBoxes, clickConnectMode,
      isActiveRef, saveRef, setSelectedBoxes, setBoxes, setArrows, setClickConnectMode, setContextMenu, setLinkPanel]);
}
